import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, streamAI } from "@/lib/api";
import { Loading, PlatformBadge, formatNumber, money } from "@/components/PageUI";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import { BookmarkPlus, ShieldCheck, ShieldAlert, AlertTriangle, Info, Sparkles, WandSparkles, BadgeCheck, Eye, Heart, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const flagIcon = { alert: ShieldAlert, warn: AlertTriangle, ok: ShieldCheck, info: Info };

function timeAgo(iso) {
  if (!iso) return null;
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function CreatorDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: c, isLoading } = useQuery({ queryKey: ["creator", id], queryFn: () => api.get(`/creators/${id}`).then(r => r.data) });
  const [analysis, setAnalysis] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  if (isLoading) return <Loading />;

  const explain = async () => {
    setBusy(true); setAnalysis("");
    try { await streamAI("match", { prompt: `Explain why ${c.name} is a strong creator partner.`, context: c }, x => setAnalysis(v => v + x)); }
    catch { toast.error("AI analysis could not be generated."); }
    finally { setBusy(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/creators/${id}/refresh`);
      qc.setQueryData(["creator", id], data);
      qc.invalidateQueries({ queryKey: ["usage"] });
      toast.success("Live stats refreshed");
    } catch (e) {
      const s = e.response?.status;
      if (s === 402) toast.error(e.response?.data?.detail || "You've hit your monthly live-search limit. Upgrade your plan to refresh more.");
      else toast.error(e.response?.data?.detail || "Couldn't refresh right now — please try again shortly.");
    } finally { setRefreshing(false); }
  };

  const est = c.audience_estimate;
  const auth = c.authenticity;
  const aud = c.audience || {};
  const media = (c.recent_media || []).filter(m => m && m.thumbnail);
  const growth = (c.growth && c.growth.length ? c.growth : [1, 2, 3, 4, 5]).map((v, i) => ({ i, v }));

  return <>
    <section className="profile-hero" data-testid="creator-profile-hero">
      <img src={c.image} alt={c.name} />
      <div className="profile-copy">
        <PlatformBadge platform={c.platform} testId="creator-profile-platform" />
        <h1 data-testid="creator-profile-name">{c.name} {c.verified && <BadgeCheck size={20} className="verified" />}</h1>
        <p data-testid="creator-profile-handle">{c.handle}{c.location && c.location !== "—" ? ` · ${c.location}` : ""}{c.is_live && <span className="live-badge" style={{ marginLeft: 10, display: "inline-flex", verticalAlign: "middle" }}><span className="live-dot" />LIVE DATA</span>}{c.last_refreshed_at && <em style={{ marginLeft: 10, fontSize: 12, color: "var(--muted,#8993a4)", fontStyle: "normal" }} data-testid="creator-last-refreshed">Updated {timeAgo(c.last_refreshed_at)}</em>}</p>
        <span data-testid="creator-profile-bio">{c.bio}</span>
        <div className="profile-actions">
          <button className="primary-btn" onClick={() => api.post(`/creators/${id}/save`).then(() => toast.success("Saved to CRM"))} data-testid="profile-save-button"><BookmarkPlus size={17} /> Save creator</button>
          {(c.is_live || c.source === "live") && <button className="secondary-btn" onClick={refresh} disabled={refreshing} data-testid="refresh-live-data-button"><RefreshCw size={17} className={refreshing ? "spin" : ""} />{refreshing ? "Re-pulling…" : "Refresh live data"}</button>}
          <button className="secondary-btn" onClick={explain} disabled={busy} data-testid="ai-match-analysis-button"><WandSparkles size={17} />{busy ? "Analyzing…" : "Explain match"}</button>
        </div>
      </div>
      <div className="hero-score" data-testid="creator-match-score"><Sparkles /> <b>{c.match_score}</b><span>AI fit score</span></div>
    </section>

    <section className="profile-grid">
      <article className="panel" data-testid="creator-performance-panel">
        <span className="eyebrow">PERFORMANCE</span><h2>Creator health</h2>
        <div className="big-metrics">
          <div><b>{formatNumber(c.followers)}</b><span>Followers</span></div>
          <div><b>{c.engagement_rate}%</b><span>Engagement</span></div>
          <div><b>{money(c.estimated_rate)}</b><span>Est. rate</span></div>
        </div>
        <div className="chart-small" data-testid="creator-growth-chart" style={{ minHeight: 150 }}>
          <ResponsiveContainer><AreaChart data={growth}>
            <defs><linearGradient id="creatorFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#00e5c7" stopOpacity=".38" /><stop offset="1" stopColor="#00e5c7" stopOpacity="0" /></linearGradient></defs>
            <Tooltip /><Area type="monotone" dataKey="v" stroke="#00e5c7" fill="url(#creatorFill)" strokeWidth={2} />
          </AreaChart></ResponsiveContainer>
        </div>
        <small style={{ color: "var(--muted,#8993a4)" }}>Follower trend over the last 12 periods{c.is_live ? " · estimated from live stats" : ""}.</small>
      </article>

      <article className="panel" data-testid="audience-intelligence-panel">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
          <span className="eyebrow">AUDIENCE INTELLIGENCE</span>
          {est && <em style={{ fontSize: 11, color: "var(--muted,#8993a4)" }} data-testid="audience-estimated-tag">Estimated · not verified</em>}
        </div>
        <h2>Who they influence</h2>
        <div className="audience-bars">
          <label>Women <span>{aud.women ?? 0}%</span><i><b style={{ width: `${aud.women ?? 0}%` }} /></i></label>
          <label>Men <span>{aud.men ?? 0}%</span><i><b style={{ width: `${aud.men ?? 0}%` }} /></i></label>
        </div>
        {est ? <>
          <div className="aud-col" data-testid="creator-age-breakdown" style={{ marginTop: 18 }}>
            <h5>Age</h5>
            {est.age.map(a => <div className="aud-row" key={a.range}><label>{a.range}</label><div className="aud-track"><i style={{ width: `${a.pct}%` }} /></div><b>{a.pct}%</b></div>)}
          </div>
          <div className="aud-col" data-testid="creator-top-countries" style={{ marginTop: 18 }}>
            <h5>Top countries</h5>
            {est.countries.map(co => <div className="aud-row" key={co.code}><label>{co.name}</label><div className="aud-track"><i className="geo" style={{ width: `${co.pct}%` }} /></div><b>{co.pct}%</b></div>)}
          </div>
        </> : <dl>
          <div><dt>Top age</dt><dd>{aud.top_age || "—"}</dd></div>
          <div><dt>Top market</dt><dd>{aud.top_country || "—"}</dd></div>
          <div><dt>Authentic audience</dt><dd>{aud.authentic ?? "—"}%</dd></div>
        </dl>}
      </article>

      <article className="panel" data-testid="brand-safety-panel">
        <span className="eyebrow">RISK & SAFETY</span><h2>Brand confidence</h2>
        {auth && auth.score != null ? <div className={`live-auth risk-${(auth.risk || "unknown").toLowerCase()}`} style={{ marginTop: 6 }} data-testid="creator-authenticity">
          <div className="live-auth-head"><ShieldCheck size={16} /><span>Authenticity check</span><b data-testid="creator-auth-score">{auth.score}<em>/100</em></b><span className="auth-risk" data-testid="creator-auth-risk">{auth.risk} risk</span></div>
          <ul>{auth.flags.map((fl, i) => { const Ic = flagIcon[fl.level] || Info; return <li key={i} className={`flag-${fl.level}`} data-testid={`creator-auth-flag-${i}`}><Ic size={14} />{fl.label}</li>; })}</ul>
        </div> : <>
          <div className="safety-ring"><ShieldCheck /><b>{c.brand_safety}</b><span>{c.brand_safety >= 75 ? "Low risk" : c.brand_safety >= 50 ? "Medium risk" : "High risk"}</span></div>
          <p>No material safety flags detected across recent content, captions, and audience behavior.</p>
        </>}
      </article>
    </section>

    {media.length > 0 && <section className="profile-grid" style={{ gridTemplateColumns: "1fr" }}>
      <article className="panel" data-testid="creator-recent-content">
        <span className="eyebrow">RECENT CONTENT</span><h2>Latest posts</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 12, marginTop: 14 }}>
          {media.map((m, i) => <div key={i} data-testid={`creator-media-${i}`} style={{ position: "relative", aspectRatio: "3/4", borderRadius: 12, overflow: "hidden", background: "linear-gradient(145deg,#141a26,#0b0e16)", border: "1px solid rgba(255,255,255,.08)" }}>
            <img src={m.thumbnail} alt="" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.currentTarget.style.display = "none"; }} />
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 10, background: "linear-gradient(to top,rgba(0,0,0,.78),rgba(0,0,0,.05) 62%)" }}>
              {m.caption && <span style={{ fontSize: 11, color: "#e7e7ea", lineHeight: 1.35, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", marginBottom: 7 }}>{m.caption}</span>}
              <div style={{ display: "flex", gap: 14, fontSize: 11, color: "#cfd6e4", fontWeight: 600 }}>
                {m.views != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Eye size={12} />{formatNumber(m.views)}</span>}
                {m.likes != null && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Heart size={12} />{formatNumber(m.likes)}</span>}
              </div>
            </div>
          </div>)}
        </div>
      </article>
    </section>}

    {analysis && <article className="ai-result" data-testid="ai-match-result"><div><Sparkles /><span>GPT 5.6 Terra analysis</span></div><pre>{analysis}</pre></article>}
  </>;
}
